import type { GameState } from '../state/gameState';
import type { GameEmitter } from '../events';
import type { MatchRule } from '../rules/matchRule';
import { PROJECTILE, SCORING, comboMultiplier } from '../config/gameConfig';
import { addScaledInPlace, distanceSq } from '../math/vec3';
import { applyPowerUp } from './powerUpSystem';

// Moves projectiles, expires them, and resolves hits through the active rule.
// Correct answer destroys the enemy, awards combo-multiplied points, and grows
// the streak; wrong answer fizzles and resets the streak. Any projectile
// passing near a power-up star collects it (no matching required).
export function updateProjectileSystem(
  state: GameState,
  dt: number,
  rule: MatchRule,
  emit: GameEmitter,
  addScore: (delta: number) => void,
): void {
  const hitRadiusSq = PROJECTILE.hitRadius * PROJECTILE.hitRadius;

  for (let i = state.projectiles.length - 1; i >= 0; i--) {
    const projectile = state.projectiles[i];
    projectile.ttl -= dt;
    addScaledInPlace(projectile.position, projectile.direction, projectile.speed * dt);

    // Generous pickup: shots collect any star they pass (and keep flying).
    for (let s = state.powerups.length - 1; s >= 0; s--) {
      const star = state.powerups[s];
      if (distanceSq(projectile.position, star.position) < hitRadiusSq) {
        state.powerups.splice(s, 1);
        applyPowerUp(state, star.kind, emit, star.position);
      }
    }

    const target =
      projectile.targetId != null
        ? state.enemies.find((e) => e.id === projectile.targetId)
        : undefined;

    if (target && distanceSq(projectile.position, target.position) < hitRadiusSq) {
      state.projectiles.splice(i, 1);
      if (rule.matches(projectile.weapon, target.value)) {
        state.enemies.splice(state.enemies.indexOf(target), 1);
        state.combo += 1;
        if (state.combo > state.bestCombo) state.bestCombo = state.combo;
        const multiplier = comboMultiplier(state.combo);
        emit.emit('enemyDestroyed', { enemy: target, projectile });
        addScore(SCORING.hit * multiplier);
        emit.emit('comboChanged', { combo: state.combo, multiplier });
      } else {
        if (state.combo > 0) {
          state.combo = 0;
          emit.emit('comboChanged', { combo: 0, multiplier: 1 });
        }
        emit.emit('answerWrong', { enemy: target, projectile });
      }
      continue;
    }

    if (projectile.ttl <= 0) state.projectiles.splice(i, 1);
  }
}
